import { useTranslate } from '@refinedev/core';
import {
  Card,
  Flex,
  Grid,
  Progress,
  Tabs,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type {
  PositionDetailOut,
  PublicationListOut,
} from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { PublicationIconLink } from '@/components/user_catalog/publications/PublicationIconLink';
import { orDash } from '@/components/common/show/shared';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { POSITION_SHOW_TABS } from '../constants';
import type { PositionShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<PositionShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface PositionShowHeaderProps {
  record: PositionDetailOut;
  publication?: PublicationListOut;
  activeTab: PositionShowTab;
  onTabChange: (key: string) => void;
}

export const PositionShowHeader = ({
  record,
  publication,
  activeTab,
  onTabChange,
}: PositionShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Long titles wrap instead of pushing the tabs bar out. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            <PublicationIconLink publicationId={record.publication_id}>
              {publication?.title ?? `#${record.publication_id}`}
            </PublicationIconLink>
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t('progress.positions.titles.show')} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('progress.positions.fields.product_kind')}>
              <ProductFormatTag
                value={record.product_kind}
                labels="positions"
              />
            </StatCell>
            <StatCell label={t('progress.positions.fields.product_variant')}>
              <ProductVariantTag
                value={record.product_variant}
                labels="positions"
              />
            </StatCell>
            <StatCell label={t('progress.positions.fields.total_progression')}>
              {orDash(
                record.total_progression != null && (
                  // Raw fraction on hover — the bar rounds to whole percent.
                  <Tooltip title={record.total_progression.toFixed(8)}>
                    <Progress
                      percent={Math.round(record.total_progression * 100)}
                      style={{ width: 180, marginBottom: 0 }}
                    />
                  </Tooltip>
                )
              )}
            </StatCell>
            <StatCell label={t('progress.positions.fields.device_id')}>
              {orDash(
                record.device_id && (
                  <Text code style={{ fontSize: token.fontSize }}>
                    {record.device_id}
                  </Text>
                )
              )}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={POSITION_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
